/**
 * Document–matter link metadata store for the Word add-in.
 *
 * Persists the link between the open .docx and its Sharedo work item using
 * two complementary stores:
 *
 *  1. Custom XML Part (primary) — embedded in the .docx, travels with the file.
 *     Word Online silently strips these on save, so we always write the fallback too.
 *
 *  2. Custom Document Properties (fallback) — stored in docProps/custom.xml,
 *     survives Word Online saves.
 *
 * Read strategy: try XML Part first, fall back to document properties, return null
 * if neither is present (document not yet linked).
 *
 * @module office/document-metadata
 */

import type { DocumentLink } from '@alterspective/shared';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const XML_NAMESPACE = 'https://alterspective.com.au/advice/v1';
const XML_PREFIX = 'advice';

const PROP_MATTER_ID = 'alterspective.matterId';
const PROP_MATTER_REF = 'alterspective.matterRef';
const PROP_LINKED_AT = 'alterspective.linkedAt';
const PROP_LINKED_BY = 'alterspective.linkedBy';
const PROP_LINK_SOURCE = 'alterspective.linkSource';

// ---------------------------------------------------------------------------
// Platform detection
// ---------------------------------------------------------------------------

/**
 * Returns true when the add-in is running inside Word Online (browser).
 * Custom XML Parts are silently stripped by Word Online on save, so callers
 * can show a warning banner when this returns true.
 */
export function detectWordOnline(): boolean {
  if (typeof Office === 'undefined' || !Office.context?.platform) return false;
  return Office.context.platform === Office.PlatformType.OfficeOnline;
}

// ---------------------------------------------------------------------------
// Read
// ---------------------------------------------------------------------------

/**
 * Read the matter link from the active document.
 *
 * Tries the Custom XML Part first, then Custom Document Properties.
 * Returns null if the document has not been linked to a matter.
 */
export async function readMatterLink(): Promise<DocumentLink | null> {
  if (typeof Word === 'undefined') {
    // Mock environment — return a sample link so panels render correctly
    return {
      matterId: 'wi-001',
      matterReference: 'WC.24.03029',
      linkedAt: '2026-01-01T00:00:00Z',
      linkedBy: 'demo@example.com',
      linkSource: 'generated',
    };
  }

  // 1. Try Custom XML Part
  try {
    const fromXml = await readFromCustomXmlPart();
    if (fromXml) return fromXml;
  } catch {
    // XML Part not available or corrupt — fall through to properties
  }

  // 2. Try Custom Document Properties
  try {
    const fromProps = await readFromCustomProperties();
    if (fromProps) return fromProps;
  } catch {
    // Properties not available
  }

  return null;
}

// ---------------------------------------------------------------------------
// Write
// ---------------------------------------------------------------------------

/**
 * Write a matter link to both stores in the active document.
 *
 * Always writes to both Custom XML Part and Custom Document Properties so
 * the link survives both desktop Word saves and Word Online saves.
 */
export async function writeMatterLink(link: DocumentLink): Promise<void> {
  if (typeof Word === 'undefined') return; // mock — no-op

  const errors: string[] = [];

  try {
    await writeToCustomXmlPart(link);
  } catch (err) {
    errors.push(`XML Part write failed: ${err instanceof Error ? err.message : String(err)}`);
  }

  try {
    await writeToCustomProperties(link);
  } catch (err) {
    errors.push(`Properties write failed: ${err instanceof Error ? err.message : String(err)}`);
  }

  if (errors.length === 2) {
    // Both stores failed — throw so the panel can surface the error
    throw new Error(`Could not persist matter link: ${errors.join('; ')}`);
  }
}

// ---------------------------------------------------------------------------
// Custom XML Part helpers
// ---------------------------------------------------------------------------

function buildXml(link: DocumentLink): string {
  return [
    `<?xml version="1.0" encoding="UTF-8"?>`,
    `<${XML_PREFIX} xmlns="${XML_NAMESPACE}">`,
    `  <matterId>${escapeXml(link.matterId)}</matterId>`,
    `  <matterReference>${escapeXml(link.matterReference)}</matterReference>`,
    `  <linkedAt>${escapeXml(link.linkedAt)}</linkedAt>`,
    `  <linkedBy>${escapeXml(link.linkedBy)}</linkedBy>`,
    `  <linkSource>${escapeXml(link.linkSource)}</linkSource>`,
    `</${XML_PREFIX}>`,
  ].join('\n');
}

async function readFromCustomXmlPart(): Promise<DocumentLink | null> {
  return Word.run(async (context) => {
    const parts = context.document.customXmlParts;
    parts.load('items');
    await context.sync();

    for (const part of parts.items) {
      part.load('namespaceUri');
      await context.sync();

      if (part.namespaceUri !== XML_NAMESPACE) continue;

      const xmlNode = part.getXml();
      await context.sync();

      return parseXml(xmlNode.value);
    }

    return null;
  });
}

async function writeToCustomXmlPart(link: DocumentLink): Promise<void> {
  return Word.run(async (context) => {
    const parts = context.document.customXmlParts;
    parts.load('items');
    await context.sync();

    // Remove any existing parts with our namespace
    for (const part of parts.items) {
      part.load('namespaceUri');
      await context.sync();
      if (part.namespaceUri === XML_NAMESPACE) {
        part.delete();
      }
    }

    await context.sync();
    context.document.customXmlParts.add(buildXml(link));
    await context.sync();
  });
}

function parseXml(xml: string): DocumentLink | null {
  const get = (tag: string): string => {
    const match = xml.match(new RegExp(`<${tag}>([^<]*)</${tag}>`));
    return match ? unescapeXml(match[1]) : '';
  };

  const matterId = get('matterId');
  if (!matterId) return null;

  const linkSource = get('linkSource');
  return {
    matterId,
    matterReference: get('matterReference'),
    linkedAt: get('linkedAt'),
    linkedBy: get('linkedBy'),
    linkSource: (linkSource as DocumentLink['linkSource']) || 'manual',
  };
}

function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function unescapeXml(s: string): string {
  return s
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'");
}

// ---------------------------------------------------------------------------
// Custom Document Properties helpers
// ---------------------------------------------------------------------------

async function readFromCustomProperties(): Promise<DocumentLink | null> {
  return new Promise((resolve, reject) => {
    // getCustomPropertiesAsync lives on the Office.js document object (not Word API).
    // result.value is Office.CustomProperties — typed as any to avoid incorrect declaration conflict.
    (Office.context.document as any).getCustomPropertiesAsync((result: Office.AsyncResult<any>) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        reject(new Error(result.error?.message ?? 'getCustomPropertiesAsync failed'));
        return;
      }

      const props: { getItem(name: string): string | null } = result.value;
      const matterId = props.getItem(PROP_MATTER_ID);
      if (!matterId) {
        resolve(null);
        return;
      }

      const linkSource = (props.getItem(PROP_LINK_SOURCE) as string) || 'manual';
      resolve({
        matterId,
        matterReference: (props.getItem(PROP_MATTER_REF) as string) ?? '',
        linkedAt: (props.getItem(PROP_LINKED_AT) as string) ?? '',
        linkedBy: (props.getItem(PROP_LINKED_BY) as string) ?? '',
        linkSource: linkSource as DocumentLink['linkSource'],
      });
    });
  });
}

async function writeToCustomProperties(link: DocumentLink): Promise<void> {
  return new Promise((resolve, reject) => {
    (Office.context.document as any).getCustomPropertiesAsync((result: Office.AsyncResult<any>) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        reject(new Error(result.error?.message ?? 'getCustomPropertiesAsync failed'));
        return;
      }

      const props: { set(name: string, value: string): void; saveAsync(cb: (r: Office.AsyncResult<void>) => void): void } = result.value;
      props.set(PROP_MATTER_ID, link.matterId);
      props.set(PROP_MATTER_REF, link.matterReference);
      props.set(PROP_LINKED_AT, link.linkedAt);
      props.set(PROP_LINKED_BY, link.linkedBy);
      props.set(PROP_LINK_SOURCE, link.linkSource);

      props.saveAsync((saveResult: Office.AsyncResult<void>) => {
        if (saveResult.status !== Office.AsyncResultStatus.Succeeded) {
          reject(new Error(saveResult.error?.message ?? 'saveAsync failed'));
        } else {
          resolve();
        }
      });
    });
  });
}
